import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { gamesApi } from '../api/gamesApi';
import { Game, GamesState } from '../types/game';

const initialState: GamesState = {
  catalogGames: [],
  searchResults: [],
  allGames: [],
  status: 'idle',
  error: null,
  currentGame: null,
};

export const fetchGames = createAsyncThunk<Game[]>(
  'games/fetchGames',
  async (_, { rejectWithValue }) => {
    try {
      const response = await gamesApi.getAllGames();
      return response;
    } catch (error) {
      return rejectWithValue('Failed to fetch games');
    }
  }
);

export const fetchGameById = createAsyncThunk<Game, number>(
  'games/fetchGameById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await gamesApi.getGameById(id);
      if (!response) {
        return rejectWithValue('Game not found');
      }
      return response;
    } catch (error) {
      return rejectWithValue('Failed to fetch game');
    }
  }
);

export const searchGames = createAsyncThunk<Game[], string>(
  'games/searchGames',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      if (!searchTerm.trim()) {
        return [];
      }
      const response = await gamesApi.searchGames(searchTerm);
      return response;
    } catch (error) {
      return rejectWithValue('Failed to search games');
    }
  }
);

const gamesSlice = createSlice({
  name: 'games',
  initialState,
  reducers: {
    clearCurrentGame: (state) => {
      state.currentGame = null;
    },
    resetCatalogToAllGames: (state) => {
      state.catalogGames = state.allGames;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGames.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchGames.fulfilled, (state, action: PayloadAction<Game[]>) => {
        state.status = 'succeeded';
        state.catalogGames = action.payload;
        state.allGames = action.payload;
        state.searchResults = [];
        state.error = null;
      })
      .addCase(fetchGames.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Something went wrong';
      })
      
      .addCase(fetchGameById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchGameById.fulfilled, (state, action: PayloadAction<Game>) => {
        state.status = 'succeeded';
        state.currentGame = action.payload;
        state.error = null;
      })
      .addCase(fetchGameById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to fetch game';
      })
      
      .addCase(searchGames.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(searchGames.fulfilled, (state, action: PayloadAction<Game[]>) => {
        state.status = 'succeeded';
        state.searchResults = action.payload;
        state.error = null;
      })
      .addCase(searchGames.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to search games';
      });
  },
});

export const { clearCurrentGame, resetCatalogToAllGames, clearSearchResults } = gamesSlice.actions;
export default gamesSlice.reducer;